import React, { useState, useEffect } from 'react';
import {
  BUILTIN_REMINDERS,
  migrateAssignment, emptySingleDetail, reminderAnchor,
} from './assignmentUtils';
import { scopedKey } from './scope';
import Icon from './Icon';
import './ReminderAssignments.css';

// The built-in reminder defs (assignmentUtils) still carry emoji `icon` values; map
// them to the semantic Icon keys for rendering. Unknown/custom defaults → 'type-custom'.
const REMINDER_ICON_KEY = {
  '🔑': 'type-new-closing',
  '🏡': 'type-sale-anniversary', '🎂': 'type-birthday', '🎉': 'type-new-year',
  '💝': 'type-valentines', '🎆': 'type-july4', '🦃': 'type-thanksgiving',
  '🎄': 'type-christmas', '📝': 'type-custom',
};
const iconKeyFor = (emoji) => REMINDER_ICON_KEY[emoji] || 'type-custom';

// Pure bulk-assign computation: returns the updated assignments map (does NOT touch
// localStorage) so callers can drive it through their own state. Bulk assignment writes
// an explicit per-contact override (wins over Auto-Enroll).
export function computeBulkAssign(prev, reminderIds, contactIds, contacts, customTemplates) {
  const todayStr = new Date().toISOString().split('T')[0];
  const fiveYears = new Date();
  fiveYears.setFullYear(fiveYears.getFullYear() + 5);
  const fiveYearsStr = fiveYears.toISOString().split('T')[0];
  let birthdaySkips = 0;
  const updated = { ...(prev || {}) };
  for (const cid of contactIds) {
    const contact = (contacts || []).find(c => c.id === cid);
    const asgn = migrateAssignment(updated[cid]);
    for (const rid of reminderIds) {
      const anchorId = reminderAnchor(rid, customTemplates);
      if (anchorId === 'happy-birthday' && !contact?.birthday) birthdaySkips++;
      // Re-assigning a previously-off reminder starts a fresh occurrence (new cycleId)
      // so it can send again even within the same day/interval.
      const reset = (cur) => (cur.assigned ? {} : { lastRunAt: '', lastRunDate: '', cycleId: Date.now() });
      // PLAIN custom (no anchor) → interval-driven; default cadence from the reminder.
      if (rid.startsWith('custom__') && !anchorId) {
        const cur = typeof asgn[rid] === 'object' ? asgn[rid] : {};
        const tmpl = (customTemplates || []).find(c => c.id === rid);
        asgn[rid] = { ...cur, ...reset(cur), assigned: true, explicit: true,
          startDate: cur.startDate || todayStr, endDate: cur.endDate || fiveYearsStr, interval: cur.interval || (tmpl && tmpl.interval) || 'One Time' };
      } else {
        // Built-in OR a copy of a built-in (anchored) → annual, date-anchored.
        const cur = typeof asgn[rid] === 'object' ? asgn[rid] : emptySingleDetail();
        asgn[rid] = { ...cur, ...reset(cur), assigned: true, explicit: true,
          startDate: cur.startDate || todayStr, interval: cur.interval || 'Annual' };
      }
    }
    updated[cid] = asgn;
  }
  return { updated, birthdaySkips };
}

// Pure bulk-UNASSIGN: removes the given reminders from the given contacts.
// If a reminder is Auto-Enrolled we must write an explicit "excluded" override to
// stop it; otherwise we just drop the assignment entirely (clean, no override badge).
export function computeBulkUnassign(prev, reminderIds, contactIds, autoMap) {
  const updated = { ...(prev || {}) };
  for (const cid of contactIds) {
    const asgn = migrateAssignment(updated[cid]);
    for (const rid of reminderIds) {
      if (autoMap && autoMap[rid] === true) {
        const cur = typeof asgn[rid] === 'object' ? asgn[rid] : {};
        asgn[rid] = { ...cur, assigned: false, explicit: true };
      } else {
        delete asgn[rid];
      }
    }
    updated[cid] = asgn;
  }
  return { updated };
}

/* ── Bulk Assign Panel ── */
export function BulkAssignPanel({ contacts, customTemplates, onApply, onUnassign }) {
  const [selReminders, setSelReminders] = useState([]);
  const [selContacts, setSelContacts]   = useState([]);
  const [feedback, setFeedback]         = useState('');
  const [conSearch, setConSearch]       = useState('');

  // Only reminders this scope actually OWNS — i.e. built-ins it inherited (has an entry
  // for) plus its own custom reminders. Built-ins the level(s) above never published down
  // aren't owned here, so they must NOT appear (you can't assign what you can't see).
  // Inherited (read-only) templates are not assignable — make a copy first.
  const ownsBuiltin = (id) => (customTemplates || []).some(c => c.builtin && c.id === id && !c.inherited);
  const allReminders = [
    ...BUILTIN_REMINDERS.filter(r => ownsBuiltin(r.id)).map(r => ({ id: r.id, label: r.label, icon: r.icon })),
    ...(customTemplates || []).filter(c => !c.builtin && !c.base && !c.inherited).map(c => ({ id: c.id, label: c.label, icon: c.anchor ? (BUILTIN_REMINDERS.find(r => r.id === c.anchor)?.icon || '📝') : '📝' })),
  ];

  // Contact list is searchable so picking from hundreds of contacts is manageable.
  const cq = conSearch.toLowerCase().trim();
  const shownContacts = cq
    ? contacts.filter(c =>
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(cq) ||
        (c.email || '').toLowerCase().includes(cq))
    : contacts;

  const allRemSelected = selReminders.length === allReminders.length && allReminders.length > 0;
  const allConSelected = shownContacts.length > 0 && shownContacts.every(c => selContacts.includes(c.id));

  function toggle(list, setList, id) {
    setFeedback('');
    setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  // Select-all toggles the currently-shown (filtered) contacts.
  function toggleAllShown() {
    setFeedback('');
    const ids = shownContacts.map(c => c.id);
    setSelContacts(prev => allConSelected ? prev.filter(id => !ids.includes(id)) : [...new Set([...prev, ...ids])]);
  }

  function handleAssign() {
    const result = onApply(selReminders, selContacts);
    let msg = `✓ Assigned ${selReminders.length} reminder${selReminders.length !== 1 ? 's' : ''} to ${selContacts.length} contact${selContacts.length !== 1 ? 's' : ''}.`;
    if (result.birthdaySkips > 0) {
      msg += ` (${result.birthdaySkips} Happy Birthday assignment${result.birthdaySkips !== 1 ? 's' : ''} will activate once a birthday is set.)`;
    }
    setFeedback(msg);
    setSelReminders([]);
    setSelContacts([]);
  }

  function handleUnassign() {
    onUnassign(selReminders, selContacts);
    setFeedback(`✗ Unassigned ${selReminders.length} reminder${selReminders.length !== 1 ? 's' : ''} from ${selContacts.length} contact${selContacts.length !== 1 ? 's' : ''}.`);
    setSelReminders([]);
    setSelContacts([]);
  }

  return (
    <div className="ra-bulk-panel">
      <div className="ra-bulk-title"><Icon name="bulk" size={18} /> Bulk Assign</div>
      <div className="ra-bulk-cols">
        <div className="ra-bulk-col">
          <div className="ra-bulk-col-head">
            <span>Reminders</span>
            <button className="ra-bulk-selall"
              onClick={() => { setFeedback(''); setSelReminders(allRemSelected ? [] : allReminders.map(r => r.id)); }}>
              {allRemSelected ? 'Clear' : 'Select all'}
            </button>
          </div>
          <div className="ra-bulk-chiplist">
            {allReminders.map(r => (
              <label key={r.id} className={`ra-bulk-chip ${selReminders.includes(r.id) ? 'selected' : ''}`}>
                <input type="checkbox" checked={selReminders.includes(r.id)}
                  onChange={() => toggle(selReminders, setSelReminders, r.id)} />
                <span className="ra-bulk-chip-icon"><Icon name={iconKeyFor(r.icon)} size={20} /></span>
                <span>{r.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="ra-bulk-col">
          <div className="ra-bulk-col-head">
            <span>Contacts</span>
            <button className="ra-bulk-selall" onClick={toggleAllShown}>
              {allConSelected ? 'Clear' : 'Select all'}
            </button>
          </div>
          <input className="ra-bulk-search" type="text" placeholder="🔍 Search contacts…"
            value={conSearch} onChange={e => { setConSearch(e.target.value); setFeedback(''); }} />
          <div className="ra-bulk-contactlist">
            {shownContacts.length === 0 ? (
              <div className="ra-bulk-noresults">No contacts match “{conSearch}”.</div>
            ) : shownContacts.map(c => (
              <label key={c.id} className={`ra-bulk-contact ${selContacts.includes(c.id) ? 'selected' : ''}`}>
                <input type="checkbox" checked={selContacts.includes(c.id)}
                  onChange={() => toggle(selContacts, setSelContacts, c.id)} />
                <span className="ra-bulk-contact-name">{c.firstName} {c.lastName}</span>
                <span className="ra-bulk-contact-email">{c.email}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="ra-bulk-footer">
        {feedback && <span className="ra-bulk-msg">{feedback}</span>}
        <span className="ra-bulk-summary">
          {selReminders.length} reminder{selReminders.length !== 1 ? 's' : ''} × {selContacts.length} contact{selContacts.length !== 1 ? 's' : ''}
        </span>
        <button className="rt-save-btn" disabled={!selReminders.length || !selContacts.length}
          onClick={handleAssign}>
          Assign
        </button>
        <button className="ra-bulk-unassign-btn" disabled={!selReminders.length || !selContacts.length}
          onClick={handleUnassign}>
          Unassign
        </button>
      </div>
    </div>
  );
}

/* ── Main Page ── */
function BulkAssign({ scope }) {
  const K_CONTACTS    = scopedKey('ira_contacts', scope);
  const K_CUSTOM      = scopedKey('ira_custom_templates', scope);
  const K_ASSIGNMENTS = scopedKey('ira_assignments', scope);
  const [contacts, setContacts] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);

  useEffect(() => {
    try { setContacts(JSON.parse(localStorage.getItem(K_CONTACTS) || '[]')); } catch { setContacts([]); }
    try { setCustomTemplates(JSON.parse(localStorage.getItem(K_CUSTOM) || '[]')); } catch { setCustomTemplates([]); }
  }, [K_CONTACTS, K_CUSTOM]);

  function applyBulkAssign(reminderIds, contactIds) {
    let prev;
    try { prev = JSON.parse(localStorage.getItem(K_ASSIGNMENTS) || '{}'); } catch { prev = {}; }
    const { updated, birthdaySkips } = computeBulkAssign(prev, reminderIds, contactIds, contacts, customTemplates);
    localStorage.setItem(K_ASSIGNMENTS, JSON.stringify(updated));
    return { birthdaySkips };
  }

  return (
    <div className="ra-container">
      <div className="ra-header">
        <h2>Assign</h2>
        <p>Assign one or more reminders to multiple contacts in a single action.</p>
      </div>

      {contacts.length === 0 ? (
        <div className="ra-empty">
          <span><Icon name="contacts" size={20} /></span>
          <p>No contacts yet. Import or add contacts first.</p>
        </div>
      ) : (
        <BulkAssignPanel
          contacts={contacts}
          customTemplates={customTemplates}
          onApply={applyBulkAssign}
        />
      )}
    </div>
  );
}

export default BulkAssign;
